import { ApiError, asyncJsonParseBytes, logFetchInit } from "@tsmono/util";

import { PendingSampleUrls, SampleData, SegmentRef } from "../api/types";

import { openZipFileFromBuffer } from "./remoteZipFile";

// Exported for the poll loop/tests. Raised from 25: the browser caps concurrent connections per host, so this trades fewer presigned-URL round trips for less frequent renderer yields, not more parallelism.
export const SEGMENT_CAP_PER_CALL = 100;

// The browser reached the view server for presigned URLs but couldn't fetch the
// segment bytes directly from storage (e.g. missing bucket CORS on the viewer
// origin, or blocked network). Distinct from other failures so callers can
// degrade to the proxy transport, which streams the same bytes same-origin.
export class DirectFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "DirectFetchError";
  }
}

export type GetPendingSampleDataUrls = (
  log_file: string,
  id: string | number,
  epoch: number,
  last_event?: number,
  last_attachment?: number,
  last_message_pool?: number,
  last_call_pool?: number,
  max_segments?: number
) => Promise<PendingSampleUrls>;

export interface DirectPendingResult {
  sampleData: SampleData;
  has_more: boolean;
  complete: boolean;
}

/**
 * Fetch one chunk of pending-sample data via presigned S3 URLs.
 * Returns `undefined` when this transport isn't supported (404, or any
 * segment lacks a presigned URL); all other failures throw.
 */
export const fetchPendingSampleDataDirect = async (
  getUrls: GetPendingSampleDataUrls,
  log_file: string,
  id: string | number,
  epoch: number,
  cursors: {
    last_event?: number;
    last_attachment?: number;
    last_message_pool?: number;
    last_call_pool?: number;
  }
): Promise<DirectPendingResult | undefined> => {
  let urls: PendingSampleUrls;
  try {
    urls = await getUrls(
      log_file,
      id,
      epoch,
      cursors.last_event,
      cursors.last_attachment,
      cursors.last_message_pool,
      cursors.last_call_pool,
      SEGMENT_CAP_PER_CALL
    );
  } catch (e) {
    // 404 = endpoint missing on this server; treat as "not supported".
    if (e instanceof ApiError && e.status === 404) {
      return undefined;
    }
    throw e;
  }

  if (urls.segments.length === 0) {
    return {
      sampleData: {
        events: [],
        attachments: [],
        message_pool: [],
        call_pool: [],
      },
      has_more: urls.has_more === true,
      complete: urls.complete === true,
    };
  }

  const canDirect = urls.segments.every((s) => s.direct_url != null);
  if (!canDirect) {
    return undefined;
  }

  // Fetch concurrently but concatenate in segment order — downstream code
  // expects events/attachments in id order.
  const parts: SampleData[] = await Promise.all(
    urls.segments.map((seg: SegmentRef) => readSegment(seg))
  );
  const out: SampleData = {
    events: parts.flatMap((p) => p.events),
    attachments: parts.flatMap((p) => p.attachments),
    message_pool: parts.flatMap((p) => p.message_pool),
    call_pool: parts.flatMap((p) => p.call_pool),
  };
  return {
    sampleData: applyCursorFilter(out, cursors),
    has_more: urls.has_more === true,
    complete: urls.complete === true,
  };
};

const readSegment = async (seg: SegmentRef): Promise<SampleData> => {
  const url = seg.direct_url;
  if (!url) throw new Error("segment has no direct_url");
  // Whole-zip fetch: segments contain one member per (sample, epoch), so
  // the zip is ~the member plus trivial framing — ranging buys nothing.
  let bytes: Uint8Array;
  try {
    const resp = await fetch(url, logFetchInit);
    // fetch() never rejects on HTTP status, so surface a non-ok response as a
    // failed direct fetch too (falls back to proxy, same as a rejection).
    if (!resp.ok) {
      throw new DirectFetchError(
        `Failed to fetch segment: ${resp.status} ${resp.statusText}`
      );
    }
    bytes = new Uint8Array(await resp.arrayBuffer());
  } catch (e) {
    if (e instanceof DirectFetchError) {
      throw e;
    }
    // fetch()/body reads reject with an opaque TypeError for any network-layer
    // failure (CORS, DNS, connection, blocked) and never for HTTP status — we
    // can't tell which, so treat any failure as "direct unusable" and use proxy.
    throw new DirectFetchError(`Failed to fetch segment: ${String(e)}`, {
      cause: e,
    });
  }
  const zip = await openZipFileFromBuffer(bytes);
  const memberBytes = await zip.readFile(seg.member_name);
  return await asyncJsonParseBytes(memberBytes);
};

// Over-inclusive segment filter -> per-item filter here. Mirrors
// SampleBufferFilestore.get_sample_data. `-1` acts as "no cursor".
const applyCursorFilter = (
  out: SampleData,
  cursors: {
    last_event?: number;
    last_attachment?: number;
    last_message_pool?: number;
    last_call_pool?: number;
  }
): SampleData => {
  const lastEvent = cursors.last_event ?? -1;
  const lastAttachment = cursors.last_attachment ?? -1;
  const lastMessagePool = cursors.last_message_pool ?? -1;
  const lastCallPool = cursors.last_call_pool ?? -1;
  out.events = out.events.filter((e) => e.id > lastEvent);
  out.attachments = out.attachments.filter((a) => a.id > lastAttachment);
  out.message_pool = out.message_pool.filter((m) => m.id > lastMessagePool);
  out.call_pool = out.call_pool.filter((c) => c.id > lastCallPool);
  return out;
};
